import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import { INICIA_SERIE, SALVA_SERIE, ATUALIZA_SERIE, BUSCA_SERIE_EDICAO, REMOVE_SERIE, LISTA_SERIE, ON_HIDE_LOADER, LISTA_ESCOLAS_SERIE, LISTA_CURSOS_POR_ESCOLA_SERIE } from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import { showAuthMessage, listaEscolasSerieSucesso, listaCursosPorEscolaSerieSucesso } from "actions/Serie";
import { salvaSerieSucesso, removeSerieSucesso, inicioSerieSucesso, buscaSerieEdicaoSucesso, listaSerieSucesso } from "actions/Serie";

/*#################################################################################################################*/
/* ---------------------------------- Helpers de Formatação --------------------------------------------------------*/

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  // Trata booleanos explicitamente para cenários numéricos (ex.: Concluinte 0/1)
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof v === 'object') {
    const cand = v.id ?? v.value ?? (typeof v.valor !== 'undefined' ? v.valor : undefined);
    if (cand === undefined || cand === null) return null;
    return toNumberOrNull(cand);
  }
  return null;
};

const extractIdNumber = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'object') {
    if (typeof v.value === 'object') return toNumberOrNull(v.value.id ?? v.value.value ?? v.value);
    return toNumberOrNull(v.id ?? v.value ?? v);
  }
  return toNumberOrNull(v);
};

const normalizeSeriePayload = (p) => {
  if (!p || typeof p !== 'object') return p;
  const out = {};
  out.Id = toNumberOrNull(p.Id ?? p.id);
  out.Codigo = p.Codigo ?? p.codigo ?? '';
  out.Nome = p.Nome ?? p.nome ?? '';
  out.Descricao = p.Descricao ?? p.descricao ?? '';
  out.CargaHoraria = toNumberOrNull(p.CargaHoraria ?? p.cargaHoraria ?? p.carga_horaria);
  out.CursoId = extractIdNumber(p.CursoId ?? p.cursoId ?? p.curso_id);
  out.DiasLetivos = toNumberOrNull(p.DiasLetivos ?? p.diasLetivos ?? p.dias_letivos);
  out.IdadeIdeal = toNumberOrNull(p.IdadeIdeal ?? p.idadeIdeal ?? p.idade_ideal);
  out.IdadeInicial = toNumberOrNull(p.IdadeInicial ?? p.idadeInicial ?? p.idade_inicial);
  out.IdadeFinal = toNumberOrNull(p.IdadeFinal ?? p.idadeFinal ?? p.idade_final);
  out.EtapaCurso = p.EtapaCurso ?? p.etapaCurso ?? p.etapa_curso ?? '';
  // Concluinte pode vir como boolean (ComboBox), normalizamos para 0/1
  const conclRaw = p.Concluinte ?? p.concluinte;
  if (typeof conclRaw === 'boolean') {
    out.Concluinte = conclRaw ? 1 : 0;
  } else if (typeof conclRaw === 'string') {
    const lc = conclRaw.toLowerCase();
    out.Concluinte = (lc === 'true' || lc === '1') ? 1 : (lc === 'false' || lc === '0') ? 0 : toNumberOrNull(conclRaw ?? 1);
  } else {
    out.Concluinte = toNumberOrNull(conclRaw ?? 1);
  }
  out.AlertaFaixaEtaria = !!(p.AlertaFaixaEtaria ?? p.alertaFaixaEtaria ?? p.alerta_faixa_etaria);
  out.BloquearMatriculaFaixaEtaria = !!(p.BloquearMatriculaFaixaEtaria ?? p.bloquearMatriculaFaixaEtaria ?? p.bloquear_matricula_faixa_etaria);
  return out;
};

const normalizeFiltro = (f) => {
  const out = {};
  const toParam = (v) => {
    if (v === undefined || v === null) return undefined;
    if (typeof v === 'object') return extractIdNumber(v); // Garante extração de ID se for objeto
    const n = Number(v);
    if (isFinite(n)) return n === 0 ? undefined : n;
    return v;
  };
  out.EscolaId = toParam(f.EscolaId || f.escolaId);
  out.CursoId = toParam(f.CursoId || f.cursoId);
  const nome = f.nome || f.Nome || f.Texto || undefined;
  if (nome && typeof nome === 'string') out.Nome = nome; // Garante que Nome seja string
  return out;
};

/*#################################################################################################################*/
/* --------------------------------------------- Requests do tipo GET ---------------------------------------------*/

const getSeries = async () =>
  await requisicaoApi.get('/series')
    .then(resposta => resposta)
    .catch(error => error);

const getSerieEdicao = async (id) =>
  await requisicaoApi.get('/series/' + id)
    .then(resposta => resposta)
    .catch(error => error);

// Carrega regras de avaliação vinculadas à série
const getRegrasAvaliacao = async (id) =>
  await requisicaoApi.get(`/series/${id}/regras`)
    .then(resposta => resposta)
    .catch(error => error);

const getListaSerie = async (filtros) => {
  let params = {};
  if (typeof filtros === 'string') {
    params.Nome = filtros;
  } else if (filtros) {
    params = normalizeFiltro(filtros);
  }

  return await requisicaoApi.get('/series', { params })
    .then(resposta => resposta)
    .catch(error => error);
};

const getEscolas = async () =>
  await requisicaoApi.get('/escolas')
    .then(resposta => resposta)
    .catch(error => error);

const getCursosDaEscola = async (idEscola) =>
  await requisicaoApi.get(`/escolas/${idEscola}/cursos`)
    .then(resposta => resposta)
    .catch(error => error);

const getTodosCursos = async () =>
  await requisicaoApi.get('/cursos')
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------- Requests do tipo POST, PUT E DELETE --------------------------------------*/

const adicionaSerieRequest = async (serie) =>
  await requisicaoApi.post('/series', serie)
    .then(resposta => resposta)
    .catch(error => error);

const atualizaSerieRequest = async (serie) =>
  await requisicaoApi.put('/series/' + (serie.id || serie.Id || ''), serie)
    .then(resposta => resposta)
    .catch(error => error);

const deleteSerieRequest = async (id) =>
  await requisicaoApi.delete('/series/' + id)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------------ Funções das Sagas ---------------------------------------------------*/

const normalizeListaSeries = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.serie)) return data.serie;
  if (data && Array.isArray(data.Serie)) return data.Serie;
  if (data && typeof data === 'object') return [data];
  return [];
};

// Normaliza lista de regras de avaliação para o formato do backend
// Esperado: { id?, anoLetivo, regraAvaliacaoId, regraAvaliacaoDiferenciadaId }
const normalizeRegraAvaliacaoList = (lista) => {
  if (!Array.isArray(lista)) return [];
  return lista.map((r) => {
    const out = {};
    // Mantém id quando existente (regras já vinculadas); novas não possuem id
    out.id = toNumberOrNull(r.id ?? r.Id);
    out.anoLetivo = toNumberOrNull(r.AnoLetivo ?? r.anoLetivo ?? r.anosLetivos ?? r.AnosLetivos);
    // Novos campos baseados em IDs (entidades)
    out.regraAvaliacaoId = toNumberOrNull(r.regraAvaliacaoId ?? r.RegraAvaliacaoId ?? r.regraPadrao);
    out.regraAvaliacaoDiferenciadaId = toNumberOrNull(r.regraAvaliacaoDiferenciadaId ?? r.RegraAvaliacaoDiferenciadaId ?? r.regraAlternativa);
    return out;
  });
};

// Transforma lista do backend em formato da UI
// Backend: { anoLetivo, regraAvaliacaoId, regraAvaliacaoDiferenciadaId }
// UI: { anoLetivo, regraPadrao (ID), regraAlternativa (ID) }
const mapRegrasToUI = (lista) => {
  if (!Array.isArray(lista)) return [];
  return lista.map((r) => ({
    id: toNumberOrNull(r.id ?? r.Id),
    anoLetivo: toNumberOrNull(r.anoLetivo ?? r.AnoLetivo ?? r.anosLetivos ?? r.AnosLetivos),
    // Mapeia os IDs das entidades para os campos da UI
    regraPadrao: toNumberOrNull(r.regraAvaliacaoId ?? r.RegraAvaliacaoId ?? r.regraPadrao),
    regraAlternativa: toNumberOrNull(r.regraAvaliacaoDiferenciadaId ?? r.RegraAvaliacaoDiferenciadaId ?? r.regraAlternativa)
  }));
};

function* fnIniciaSerie() {
  try {
    const retorno = yield call(getSeries);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const listaNormalizada = normalizeListaSeries(retorno.data);
      yield put(inicioSerieSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnBuscaSerieEdicao({ payload }) {
  try {
    const retorno = yield call(getSerieEdicao, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const serieData = retorno.data || {};
      const regrasRaw = Array.isArray(serieData.regrasAvaliacao) ? serieData.regrasAvaliacao : [];
      const regrasUI = mapRegrasToUI(regrasRaw);
      const payloadMerge = { ...serieData, regrasAvaliacao: regrasUI };
      yield put(buscaSerieEdicaoSucesso(payloadMerge));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaSerie({ payload }) {
  try {
    const retorno = yield call(getListaSerie, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const listaNormalizada = normalizeListaSeries(retorno.data);
      yield put(listaSerieSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnSalvaSerie({ payload }) {
  try {
    const payloadNormalized = normalizeSeriePayload(payload);
    // Inclui subentidade RegrasAvaliacao diretamente no payload principal
    let regrasNormalized = normalizeRegraAvaliacaoList(payload.regrasAvaliacao);
    if (Array.isArray(regrasNormalized)) {
      // Na criação, não enviar 'id' nas regras para evitar erro de duplicidade
      regrasNormalized = regrasNormalized.map(({ id, Id, ...rest }) => ({ ...rest }));
      // Backend espera camelCase no corpo da Série
      payloadNormalized.regrasAvaliacao = regrasNormalized;
    }
    const retorno = yield call(adicionaSerieRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaSerieSucesso('Série salva com sucesso!'));
      yield put({ type: LISTA_SERIE, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnAtualizaSerie({ payload }) {
  try {
    const payloadNormalized = normalizeSeriePayload(payload);
    let regrasNormalized = normalizeRegraAvaliacaoList(payload.regrasAvaliacao);
    if (Array.isArray(regrasNormalized)) {
      // No PUT, manter id apenas para regras existentes; omitir quando id for nulo/indefinido
      regrasNormalized = regrasNormalized.map((r) => {
        const idNum = toNumberOrNull(r.id ?? r.Id);
        const { id, Id, ...rest } = r;
        return idNum === null ? rest : { ...rest, id: idNum };
      });
      payloadNormalized.regrasAvaliacao = regrasNormalized;
    }
    const retorno = yield call(atualizaSerieRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaSerieSucesso('Série atualizada com sucesso!'));
      yield put({ type: LISTA_SERIE, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnRemoveSerie({ payload }) {
  try {
    const retorno = yield call(deleteSerieRequest, payload);

    // Verifica se houve erro de validação (status 400 ou sucesso: false)
    const data = retorno?.response?.data || retorno?.data;

    // Tratamento para quando a API retorna erro 400 com inconsistências
    if (retorno?.response?.status === 400 || (data && data.sucesso === false)) {
      let mensagemErro = '';
      if (Array.isArray(data?.inconsistencias) && data.inconsistencias.length > 0) {
        mensagemErro = data.inconsistencias[0].mensagem;
      } else if (Array.isArray(data?.erros) && data.erros.length > 0) {
        mensagemErro = data.erros[0].mensagem;
      } else {
        mensagemErro = data?.mensagem || 'Esta série já está sendo usada por uma turma';
      }
      yield put(showAuthMessage('Esta série esta sendo usada por uma turma e não pode ser excluida'));
      return;
    }

    if (retorno.message) {
      yield put(showAuthMessage('Esta série já está sendo usada por uma turma'));
    } else {
      yield put(removeSerieSucesso('Série removida com sucesso!'));
      yield put({ type: LISTA_SERIE, payload: { nome: '' } });
    }
  } catch (error) {
    // Tenta extrair mensagem amigável de erro se disponível
    const msg = error?.response?.data?.mensagem ||
      (error?.response?.data?.inconsistencias?.[0]?.mensagem) ||
      'Esta série já está sendo usada por uma turma';
    yield put(showAuthMessage(msg));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnListaEscolasSerie() {
  try {
    const retorno = yield call(getEscolas);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(listaEscolasSerieSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaCursosPorEscolaSerie({ payload }) {
  try {
    // Tenta extrair o ID da escola de várias formas possíveis no payload
    const escolaId = payload?.escolaId || (payload && typeof payload !== 'object' ? payload : 0);

    let retorno;
    if (escolaId && Number(escolaId) > 0) {
      retorno = yield call(getCursosDaEscola, escolaId);
    } else {
      retorno = yield call(getTodosCursos);
    }

    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const listaNormalizada = Array.isArray(retorno.data) 
        ? retorno.data.map(c => ({ 
            id: c.cursoId || c.id || c.Id, 
            nome: c.cursoNome || c.nome || c.Nome 
          })) 
        : [];
      yield put(listaCursosPorEscolaSerieSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

export function* iniciaAcoesSerie() {
  yield takeEvery(INICIA_SERIE, fnIniciaSerie);
}

export function* salvaSerie() {
  yield takeEvery(SALVA_SERIE, fnSalvaSerie);
}

export function* atualizaSerie() {
  yield takeEvery(ATUALIZA_SERIE, fnAtualizaSerie);
}

export function* buscaSerieEdicao() {
  yield takeEvery(BUSCA_SERIE_EDICAO, fnBuscaSerieEdicao);
}

export function* removeSerie() {
  yield takeEvery(REMOVE_SERIE, fnRemoveSerie);
}

export function* listaSerie() {
  yield takeEvery(LISTA_SERIE, fnListaSerie);
}

export function* listaEscolasSerie() {
  yield takeEvery(LISTA_ESCOLAS_SERIE, fnListaEscolasSerie);
}

export function* listaCursosPorEscolaSerie() {
  yield takeEvery(LISTA_CURSOS_POR_ESCOLA_SERIE, fnListaCursosPorEscolaSerie);
}

export default function* rootSaga() {
  yield all([
    fork(iniciaAcoesSerie),
    fork(salvaSerie),
    fork(atualizaSerie),
    fork(buscaSerieEdicao),
    fork(removeSerie),
    fork(listaSerie),
    fork(listaEscolasSerie),
    fork(listaCursosPorEscolaSerie),
  ]);
}